import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';

import { IconButton } from '../../../cdl/IconButton/IconButton';
import { Input } from '../../../cdl/Input/Input';
import { Text } from '../../../cdl/Text/Text';
import { TextButton } from '../../../common/buttons/TextButton';
import { formatCompanyName } from '../../../common/helpers/formatCompanyName.helper';
import { formatNumber } from '../../../common/helpers/formatNumber.helper';
import { translate } from '../../../common/helpers/translate.helper';
import { useDebounce } from '../../../common/hooks/useDebounce';
import { IconInfoCircle } from '../../../common/icons/cdl/InfoCircle';
import { IconSearch } from '../../../common/icons/cdl/Search';
import { IconX } from '../../../common/icons/cdl/X';
import { InfoPopover } from '../../../common/InfoPopover/InfoPopover';
import { Box } from '../../../common/ui/Box';
import { useDemandDefinitionCalculationContext } from '../../hooks/useDemandDefinitionCalculationContext';
import { DemandDefinitionReferencePeriodFormat } from '../../types/DemandDefinitionReferencePeriodFormat';
import type { DemandDefinitionState } from '../../types/DemandDefinitionState';
import type { DemandFleetCompany } from '../../types/DemandFleetCompany';
import { DemandCardSection } from '../DemandCardSection';
import { DemandDefinitionReferencePeriod } from '../DemandDefinitionReferencePeriod';
import { DemandMetricCard } from '../DemandMetricCard';
import { DemandVolume } from '../DemandVolume';

import { FleetCompanyRow } from './FleetCompanyRow';

interface FleetScopeSummary {
    companyCount: number;
    vesselCount: number;
    historicalVolume: number;
    referencePeriodFrom?: Date;
    referencePeriodTo?: Date;
}

export const FleetScopeStep = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [lastSummary, setLastSummary] = useState<FleetScopeSummary>();
    const debouncedSearchQuery = useDebounce(searchQuery, 300);
    const { calculation, companies, isCalculationLoading, isCompanyVolumeLoading, showHistoricalData } =
        useDemandDefinitionCalculationContext();
    const { setTouched, setValues, touched, values } = useFormikContext<DemandDefinitionState>();
    const showCompanyControls = companies.length > 10;
    const isSearchActive = showCompanyControls && Boolean(searchQuery.trim());
    const activeSearchQuery = isSearchActive ? debouncedSearchQuery.trim().toLocaleLowerCase() : '';
    const visibleCompanies = activeSearchQuery
        ? companies.filter((company) =>
              (formatCompanyName({ company }) ?? company.name).toLocaleLowerCase().includes(activeSearchQuery)
          )
        : companies;
    const selectedCompanies = companies.filter((company) => !values.excludedCompanyIds.includes(company.id));
    const areAllCompaniesSelected = selectedCompanies.length === companies.length;
    const selectedVessels = selectedCompanies.flatMap((company) => company.vessels);
    const selectedCompanyCount = selectedCompanies.length;
    const currentSummary = calculation
        ? {
              companyCount: selectedCompanyCount,
              vesselCount: selectedVessels.length,
              historicalVolume: calculation.total.historicalVolume,
              referencePeriodFrom: values.referencePeriodFrom,
              referencePeriodTo: values.referencePeriodTo,
          }
        : undefined;
    const displayedSummary = isCalculationLoading ? lastSummary : currentSummary ?? lastSummary;

    useEffect(() => {
        if (!calculation || isCalculationLoading) {
            return;
        }

        setLastSummary({
            companyCount: selectedCompanyCount,
            vesselCount: selectedVessels.length,
            historicalVolume: calculation.total.historicalVolume,
            referencePeriodFrom: values.referencePeriodFrom,
            referencePeriodTo: values.referencePeriodTo,
        });
    }, [
        calculation,
        isCalculationLoading,
        selectedCompanyCount,
        selectedVessels.length,
        values.referencePeriodFrom,
        values.referencePeriodTo,
    ]);

    const toggleCompanySelection = (company: DemandFleetCompany) => {
        const isCompanySelected = !values.excludedCompanyIds.includes(company.id);

        setTouched({ ...touched, fleetChanges: [] }, false);
        setValues({
            ...values,
            excludedCompanyIds: isCompanySelected
                ? [...values.excludedCompanyIds, company.id]
                : values.excludedCompanyIds.filter((companyId) => companyId !== company.id),
            fleetChanges: [],
        });
    };

    const toggleAllCompanies = () => {
        const companyIds = new Set(companies.map((company) => company.id));

        setTouched({ ...touched, fleetChanges: [] }, false);
        setValues({
            ...values,
            excludedCompanyIds: areAllCompaniesSelected
                ? [...new Set([...values.excludedCompanyIds, ...companyIds])]
                : values.excludedCompanyIds.filter((companyId) => !companyIds.has(companyId)),
            fleetChanges: [],
        });
    };

    return (
        <Box display="flex" flexDirection="column" gap={24}>
            {showHistoricalData ? (
                <DemandCardSection isLoading={isCalculationLoading}>
                    <DemandMetricCard
                        label={translate('demandDefinition.selectedCompanies')}
                        value={
                            displayedSummary
                                ? formatNumber({ number: displayedSummary.companyCount, maximumFractionDigits: 0 })
                                : undefined
                        }
                    />
                    <DemandMetricCard
                        label={translate('demandDefinition.includedVessels')}
                        value={
                            displayedSummary
                                ? formatNumber({ number: displayedSummary.vesselCount, maximumFractionDigits: 0 })
                                : undefined
                        }
                    />
                    <DemandMetricCard
                        label={translate('demandDefinition.selectedVolume')}
                        value={
                            displayedSummary ? (
                                <Box display="flex" alignItems="center" gap={6}>
                                    <Text variant="subtitle1">
                                        <DemandVolume volume={displayedSummary.historicalVolume} />
                                    </Text>
                                    <InfoPopover Icon={IconInfoCircle} side="right" width="257px">
                                        <Box display="flex" flexDirection="column" gap={8}>
                                            <Text variant="extraSmall">
                                                {translate(
                                                    'demandDefinition.historicalVolumeReferencePeriodDescription'
                                                )}
                                            </Text>
                                            <Text variant="extraSmall">
                                                <DemandDefinitionReferencePeriod
                                                    formatVariant={DemandDefinitionReferencePeriodFormat.MONTH}
                                                    referencePeriod={{
                                                        from: displayedSummary.referencePeriodFrom,
                                                        to: displayedSummary.referencePeriodTo,
                                                    }}
                                                />
                                            </Text>
                                        </Box>
                                    </InfoPopover>
                                </Box>
                            ) : undefined
                        }
                    />
                </DemandCardSection>
            ) : null}
            <Box display="flex" flexDirection="column" paddingTop={12}>
                {showCompanyControls ? (
                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={16} marginBottom={16}>
                        <Box width={320} maxWidth="100%">
                            <Input
                                aria-label={translate('demandDefinition.searchCompanies')}
                                placeholder={translate('demandDefinition.searchCompanies')}
                                leadingVisual={<IconSearch height={16} width={16} />}
                                trailingVisual={
                                    searchQuery ? (
                                        <IconButton
                                            type="button"
                                            Icon={IconX}
                                            size="small"
                                            emphasis="transparent"
                                            aria-label={translate('demandDefinition.clearSearch')}
                                            onClick={() => setSearchQuery('')}
                                        />
                                    ) : null
                                }
                                value={searchQuery}
                                onChange={(event) => setSearchQuery(event.target.value)}
                            />
                        </Box>
                        {isSearchActive ? null : (
                            <TextButton type="button" onClick={toggleAllCompanies}>
                                {areAllCompaniesSelected
                                    ? translate('demandDefinition.unselectAllCompanies')
                                    : translate('demandDefinition.selectAllCompanies')}
                            </TextButton>
                        )}
                    </Box>
                ) : null}
                <Box
                    display="grid"
                    gridTemplateColumns={showHistoricalData ? 'minmax(0, 1fr) 140px' : 'minmax(0, 1fr)'}
                    columnGap={12}
                    alignItems="center"
                    paddingX={8}
                    marginBottom={4}
                >
                    <Text variant="fieldLabel" color="foreground.muted">
                        {translate('demandDefinition.company')}
                    </Text>
                    {showHistoricalData ? (
                        <Text variant="fieldLabel" color="foreground.muted" textAlign="right">
                            {translate('demandDefinition.historicalVolume')}
                        </Text>
                    ) : null}
                </Box>
                {visibleCompanies.map((company) => (
                    <FleetCompanyRow
                        key={company.id}
                        company={company}
                        isVolumeLoading={isCompanyVolumeLoading}
                        isSelected={!values.excludedCompanyIds.includes(company.id)}
                        showHistoricalData={showHistoricalData}
                        onToggleCompanySelection={toggleCompanySelection}
                    />
                ))}
                {activeSearchQuery && visibleCompanies.length < companies.length ? (
                    <Text variant="small" color="foreground.muted" paddingX={8} paddingY={8}>
                        {visibleCompanies.length === 0
                            ? translate('demandDefinition.noMatchingCompanies')
                            : translate('demandDefinition.companiesShownOfTotal', {
                                  count: String(visibleCompanies.length),
                                  total: String(companies.length),
                              })}
                    </Text>
                ) : null}
            </Box>
        </Box>
    );
};
